import React, { useState, useEffect } from 'react';
import { Row, Col, Form } from '@/components/ui';
import { VehicleFilters as VehicleFiltersType } from '../../../types/Vehicle';
import FiltersCard from '../../../components/FiltersCard';

interface VehicleFiltersProps {
  filters: VehicleFiltersType;
  filterOptions: {
    currentStatuses: string[];
    serviceAreas: { id: number; name: string; code?: string }[];
    vehicleClassifications: string[];
  };
  onFilterChange: (filters: VehicleFiltersType) => void;
}

const VehicleFilters: React.FC<VehicleFiltersProps> = ({ filters, filterOptions, onFilterChange }) => {
  const [localFilters, setLocalFilters] = useState<VehicleFiltersType>(filters);

  // Update local filters when props change (e.g., when cleared externally)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  const handleFilterChange = (name: keyof VehicleFiltersType, value: string) => {
    setLocalFilters({
      ...localFilters,
      [name]: value
    });
  };

  const handleApplyFilters = () => {
    onFilterChange(localFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');
  const hasPendingChanges = JSON.stringify(localFilters) !== JSON.stringify(filters);

  const handleClearFilters = () => {
    const clearedFilters = {
      currentStatus: '',
      serviceArea: '',
      vehicleClassification: '',
      search: ''
    };
    setLocalFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  return (
    <FiltersCard
      title="Vehicle Filters"
      subtitle="Filter vehicles by status, service area, and classification"
      collapsible={true}
      defaultCollapsed={false}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters}
      showApplyButton={true}
      onApply={handleApplyFilters}
      applyDisabled={!hasPendingChanges}
      applyButtonText="Apply Filters"
    >
      <Row className="tw-items-end">
        <Col md={3}>
          <Form.Label>Current Status</Form.Label>
          <Form.Select
            size="sm"
            value={localFilters.currentStatus || ''}
            onChange={(e) => handleFilterChange('currentStatus', e.target.value)}
          >
            <option value="">All Statuses</option>
            {filterOptions.currentStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Service Area</Form.Label>
          <Form.Select
            size="sm"
            value={localFilters.serviceArea || ''}
            onChange={(e) => handleFilterChange('serviceArea', e.target.value)}
          >
            <option value="">All Service Areas</option>
            {filterOptions.serviceAreas.map(area => (
              <option key={area.id} value={area.id.toString()}>{area.name}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Vehicle Classification</Form.Label>
          <Form.Select
            size="sm"
            value={localFilters.vehicleClassification || ''}
            onChange={(e) => handleFilterChange('vehicleClassification', e.target.value)}
          >
            <option value="">All Classifications</option>
            {filterOptions.vehicleClassifications.map(classification => (
              <option key={classification} value={classification}>{classification}</option>
            ))}
          </Form.Select>
        </Col>
        <Col md={3}>
          <Form.Label>Search</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            placeholder="Search by unit number, description..."
            value={localFilters.search || ''}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleApplyFilters();
              }
            }}
          />
        </Col>
      </Row>
    </FiltersCard>
  );
};

export default VehicleFilters;

